<template>
  <main class="mail-page">
    <section class="mail-panel mail-select-panel">
      <div class="mail-panel-heading">
        <div>
          <h2>{{ $t('mailReceive.purchase') }}</h2>
          <p>{{ $t('mailReceive.priceTip') }}</p>
        </div>
      </div>

      <div v-if="loadingOptions" class="mail-empty-state">
        {{ $t('mailReceive.loading') }}
      </div>
      <div v-else class="mail-form-grid">
        <label class="mail-field">
          <span>{{ $t('mailReceive.project') }}</span>
          <el-select
            v-model="selectedProjectKey"
            filterable
            clearable
            class="mail-search-select"
            popper-class="mail-search-popper"
            :disabled="projects.length === 0 || creatingOrder"
            :placeholder="$t('mailReceive.projectPlaceholder')"
          >
            <el-option
              v-for="project in projects"
              :key="`${project.supplier}-${project.serviceCode}`"
              :label="`${project.projectName} (${project.serviceCode})`"
              :value="`${project.supplier}:${project.serviceCode}`"
            />
          </el-select>
        </label>

        <label class="mail-field">
          <span>{{ $t('mailReceive.domain') }}</span>
          <el-select
            v-model="selectedDomain"
            filterable
            clearable
            class="mail-search-select"
            popper-class="mail-search-popper"
            :disabled="domains.length === 0 || creatingOrder"
            :placeholder="$t('mailReceive.domainPlaceholder')"
          >
            <el-option
              v-for="domain in domains"
              :key="domain.domain"
              :label="domain.domainName || domain.domain"
              :value="domain.domain"
            />
          </el-select>
        </label>

        <div class="mail-price-card" :aria-busy="loadingPrice">
          <span class="mail-price-label">{{ $t('mailReceive.price') }}</span>
          <span v-if="loadingPrice" class="mail-price-loading" role="status">
            <span class="mail-price-spinner" aria-hidden="true"></span>
          </span>
          <template v-else>
            <strong v-if="selectedPrice">¥{{ formatMoney(selectedPrice.userPrice) }}</strong>
            <strong v-else>—</strong>
            <span v-if="selectedPrice && selectedPrice.count > 0" class="mail-stock">
              {{ selectedPrice.count }} {{ $t('mailReceive.available') }}
            </span>
            <span v-else class="mail-no-stock">{{ $t('mailReceive.noStock') }}</span>
          </template>
        </div>

        <button
          class="mail-primary-button"
          type="button"
          :disabled="!canActivate"
          @click="createOrder"
        >
          <span v-if="creatingOrder || loadingPrice" class="mail-spinner"></span>
          <span v-else-if="selectedPrice">
            {{ $t('mailReceive.purchaseWithPrice', { price: formatMoney(selectedPrice.userPrice) }) }}
          </span>
          <span v-else>{{ $t('mailReceive.purchase') }}</span>
        </button>
      </div>

      <div v-if="!loadingOptions && projects.length === 0" class="mail-empty-state">
        {{ $t('mailReceive.emptyProjects') }}
      </div>
      <div v-if="!loadingOptions && domains.length === 0" class="mail-empty-state">
        {{ $t('mailReceive.emptyDomains') }}
      </div>
    </section>

    <section class="mail-panel mail-orders-panel">
      <div class="mail-panel-heading mail-orders-heading">
        <div>
          <h2>{{ $t('mailReceive.orderTitle') }}</h2>
          <p>{{ $t('mailReceive.waitingCode') }}</p>
        </div>
        <button class="mail-secondary-button" type="button" :disabled="loadingOrders" @click="loadOrders">
          {{ $t('mailReceive.refresh') }}
        </button>
      </div>

      <div v-if="loadingOrders && orders.length === 0" class="mail-empty-state">
        {{ $t('mailReceive.loading') }}
      </div>
      <div v-else-if="orders.length === 0" class="mail-empty-state">
        {{ $t('mailReceive.noOrders') }}
      </div>
      <div v-else class="mail-order-list">
        <article v-for="order in orders" :key="order.activationId" class="mail-order-card">
          <div class="mail-order-top">
            <div class="mail-order-title">
              <strong>{{ order.projectName || order.serviceCode }}</strong>
              <span>{{ order.domain }}</span>
            </div>
            <span class="mail-status" :class="statusClass(order.status)">
              {{ statusLabel(order.status) }}
            </span>
          </div>

          <div class="mail-order-address">
            <div class="mail-address-section">
              <span class="mail-order-label">{{ $t('mailReceive.emailAddress') }}</span>
              <div class="mail-address-value">
                <strong>{{ order.mailAddress }}</strong>
                <el-tooltip :content="$t('mailReceive.copy')" placement="top">
                  <button
                    class="mail-copy-icon"
                    type="button"
                    :aria-label="$t('mailReceive.copy')"
                    @click="copyText(order.mailAddress)"
                  >
                    <svg
                      class="mail-copy-icon-symbol"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="8" y="8" width="11" height="11" rx="1.5" />
                      <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
                    </svg>
                  </button>
                </el-tooltip>
              </div>
            </div>

            <div class="mail-code-box" :class="{ 'mail-code-ready': order.verificationCode }">
              <span class="mail-order-label">{{ $t('mailReceive.verificationCode') }}</span>
              <div class="mail-code-value">
                <strong>{{ order.verificationCode || '—' }}</strong>
                <button
                  v-if="order.verificationCode"
                  class="mail-copy-button"
                  type="button"
                  @click="copyText(order.verificationCode)"
                >
                  {{ $t('common.copy') }}
                </button>
              </div>
              <span
                v-if="order.status === 'WAITING_CODE' || order.status === 'ERROR'"
                class="mail-code-status"
              >
                {{ order.status === 'WAITING_CODE' ? $t('mailReceive.waitingCode') : $t('mailReceive.status.ERROR') }}
              </span>
            </div>
          </div>

          <div class="mail-order-meta">
            <span>{{ $t('mailReceive.price') }}：¥{{ formatMoney(order.userPrice) }}</span>
            <span>{{ $t('mailReceive.expiresIn') }}：{{ formatCountdown(order.expireTime) }}</span>
          </div>
          <p v-if="order.settlementPending" class="mail-settlement-note">
            {{ $t('mailReceive.settlementPending') }}
          </p>

          <div v-if="order.status === 'WAITING_CODE' || order.status === 'ERROR'" class="mail-order-actions">
            <button class="mail-cancel-button" type="button" @click="cancelOrder(order)">
              {{ $t('mailReceive.cancel') }}
            </button>
          </div>
        </article>
      </div>
    </section>
  </main>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import {
  cancelMailActivation,
  createMailActivation,
  getMailCode,
  getMailDomains,
  getMailOrders,
  getMailPrice,
  getMailProjects
} from '@/api/mail'
import { showErrorIfNotNotified } from '@/api/http'

export default {
  name: 'MailReceiveView',
  setup() {
    const { t: $t } = useI18n()
    const projects = ref([])
    const domains = ref([])
    const priceRows = ref([])
    const orders = ref([])
    const selectedProjectKey = ref('')
    const selectedDomain = ref('')
    const loadingOptions = ref(true)
    const loadingPrice = ref(false)
    const loadingOrders = ref(false)
    const creatingOrder = ref(false)
    const polling = ref(false)
    const currentTime = ref(Date.now())
    let pollTimer = null
    let countdownTimer = null
    let priceRequestSeq = 0

    const selectedProject = computed(() => projects.value.find(project =>
      `${project.supplier}:${project.serviceCode}` === selectedProjectKey.value
    ) || null)
    const selectedServiceCode = computed(() => selectedProject.value?.serviceCode || '')
    const selectedSupplier = computed(() => selectedProject.value?.supplier || null)
    const selectedPrice = computed(() => priceRows.value[0] || null)
    const canActivate = computed(() => {
      return Boolean(
        selectedSupplier.value &&
        selectedServiceCode.value &&
        selectedDomain.value &&
        !loadingPrice.value &&
        selectedPrice.value &&
        Number(selectedPrice.value.count) > 0 &&
        !creatingOrder.value
      )
    })

    /**
     * 将用户端订单响应归一为本地激活订单 ID，兼容旧接口返回的 id 字段。
     * @param {Object} order 服务端返回的邮箱订单
     * @returns {Object|null} 可用于轮询和取消的订单，缺少本地订单 ID 时返回空
     */
    const normalizeOrder = order => {
      if (!order || typeof order !== 'object') {
        // 响应项不是订单对象时不能进入订单操作流，避免向后端提交空 ID。
        console.error('邮箱订单响应格式错误，无法读取本地订单 ID', order)
        return null
      }
      const activationId = order.activationId ?? order.id
      if (activationId === null || activationId === undefined || activationId === '') {
        // 后端未返回可操作的本地订单 ID 时丢弃该行，防止取消接口收到 null。
        console.error('邮箱订单缺少本地订单 ID', {
          serviceCode: order.serviceCode,
          mailAddress: order.mailAddress,
          status: order.status
        })
        return null
      }
      return {
        ...order,
        activationId
      }
    }

    /**
     * 判断两个本地订单 ID 是否指向同一笔订单，兼容接口返回数字或字符串 ID。
     * @param {number|string} left 左侧订单 ID
     * @param {number|string} right 右侧订单 ID
     * @returns {boolean} 是否同一笔本地订单
     */
    const isSameActivation = (left, right) => String(left) === String(right)

    /**
     * 加载启用项目和域名，并初始化默认选择。
     * @returns {Promise<void>}
     */
    const loadOptions = async () => {
      loadingOptions.value = true
      try {
        const [projectResponse, domainResponse] = await Promise.all([
          getMailProjects(),
          getMailDomains()
        ])
        if (projectResponse.status === 200) {
          // 只从后端下发的启用项目选项生成可选列表。
          projects.value = Array.isArray(projectResponse.data) ? projectResponse.data : []
          selectedProjectKey.value = projects.value.length > 0
            ? `${projects.value[0].supplier}:${projects.value[0].serviceCode}`
            : ''
        }
        if (domainResponse.status === 200) {
          // 只从后端下发的启用域名选项生成可选列表。
          domains.value = Array.isArray(domainResponse.data) ? domainResponse.data : []
          selectedDomain.value = domains.value[0]?.domain || ''
        }
      } catch (error) {
        // HTTP 拦截器已提示请求错误时不重复弹窗。
        showErrorIfNotNotified(error, $t('mailReceive.loadFailed'))
      } finally {
        loadingOptions.value = false
      }
    }

    /**
     * 按当前项目和域名刷新实时人民币价格与库存。
     * @returns {Promise<void>}
     */
    const refreshPrice = async () => {
      if (!selectedSupplier.value || !selectedServiceCode.value || !selectedDomain.value) {
        // 选择尚未完成时清空旧报价，避免用户误按旧价格取号。
        priceRequestSeq += 1
        priceRows.value = []
        loadingPrice.value = false
        return
      }
      const requestSeq = ++priceRequestSeq
      priceRows.value = []
      loadingPrice.value = true
      try {
        const response = await getMailPrice({
          supplier: selectedSupplier.value,
          serviceCode: selectedServiceCode.value,
          domain: selectedDomain.value
        })
        if (response.status === 200 && requestSeq === priceRequestSeq) {
          // 后端价格响应已限制为人民币售价、项目、域名和库存白名单。
          priceRows.value = Array.isArray(response.data) ? response.data : []
        }
      } catch (error) {
        // 价格失败时清空报价，阻止使用旧价格继续下单。
        if (requestSeq === priceRequestSeq) {
          priceRows.value = []
          showErrorIfNotNotified(error, $t('mailReceive.priceFailed'))
        }
      } finally {
        if (requestSeq === priceRequestSeq) {
          // 仅最新选择的价格请求有权结束当前加载状态。
          loadingPrice.value = false
        }
      }
    }

    /**
     * 创建邮箱订单并把新单放入当前用户订单列表。
     * @returns {Promise<void>}
     */
    const createOrder = async () => {
      if (!canActivate.value) {
        // 没有有效报价或库存时不发起供应商取号请求。
        ElMessage.warning($t('mailReceive.noStock'))
        return
      }
      creatingOrder.value = true
      try {
        const response = await createMailActivation({
          supplier: selectedSupplier.value,
          serviceCode: selectedServiceCode.value,
          domain: selectedDomain.value
        })
        if (response.status === 200 && response.data) {
          // 下单成功后以服务端回传订单快照为准，前端不构造供应商 ID。
          const createdOrder = normalizeOrder(response.data)
          if (!createdOrder) {
            // 创建响应缺少本地订单 ID 时不渲染不可取消的订单，并明确提示本次下单结果异常。
            ElMessage.error($t('mailReceive.orderFailed'))
            return
          }
          orders.value = [
            createdOrder,
            ...orders.value.filter(order => !isSameActivation(order.activationId, createdOrder.activationId))
          ]
          ElMessage.success($t('mailReceive.orderCreated'))
        }
      } catch (error) {
        // 统一错误拦截器负责已显示的服务端提示。
        showErrorIfNotNotified(error, $t('mailReceive.orderFailed'))
      } finally {
        creatingOrder.value = false
      }
    }

    /**
     * 加载当前用户的最近邮箱订单。
     * @returns {Promise<void>}
     */
    const loadOrders = async () => {
      loadingOrders.value = true
      try {
        const response = await getMailOrders({ pageNum: 1, pageSize: 20 })
        if (response.status === 200) {
          // 订单列表由后端绑定当前 UID，页面不提交或缓存用户身份字段。
          const responseOrders = Array.isArray(response.data?.list) ? response.data.list : []
          orders.value = responseOrders.map(normalizeOrder).filter(Boolean)
        }
      } catch (error) {
        // 保留页面现有订单快照，同时给出非重复错误提示。
        showErrorIfNotNotified(error, $t('mailReceive.loadFailed'))
      } finally {
        loadingOrders.value = false
      }
    }

    /**
     * 轮询等待中的订单，并同步验证码及供应商结算状态。
     * @returns {Promise<void>}
     */
    const pollOrders = async () => {
      if (polling.value) {
        // 防止上一轮供应商请求未结束时叠加更多轮询。
        return
      }
      const waitingOrders = orders.value.filter(order => order.status === 'WAITING_CODE')
      if (waitingOrders.length === 0) {
        // 所有订单已结束时不产生多余的验证码查询请求。
        return
      }
      polling.value = true
      try {
        await Promise.all(waitingOrders.map(async order => {
          try {
            const response = await getMailCode(order.activationId)
            if (response.status === 200 && response.data) {
              // 每次轮询只更新服务端明确返回的订单状态和已计费验证码。
              updateOrder(response.data)
            }
          } catch (error) {
            // 自动轮询不连续弹出网络错误，用户仍可手动刷新订单。
            console.warn('邮箱验证码轮询失败', order.activationId, error)
          }
        }))
      } finally {
        polling.value = false
      }
    }

    /**
     * 合并单笔订单的验证码状态字段，不覆盖已有邮箱地址及计费信息。
     * @param {Object} update 服务端返回的订单状态
     */
    const updateOrder = update => {
      const normalizedUpdate = normalizeOrder(update)
      if (!normalizedUpdate) {
        // 无本地订单 ID 的状态响应不能安全合并到任何订单行。
        return
      }
      const index = orders.value.findIndex(order =>
        isSameActivation(order.activationId, normalizedUpdate.activationId))
      if (index < 0) {
        // 只更新列表中已由后端确认归属的订单。
        return
      }
      orders.value.splice(index, 1, {
        ...orders.value[index],
        ...normalizedUpdate,
        verificationCode: normalizedUpdate.verificationCode || orders.value[index].verificationCode,
        receivedTime: normalizedUpdate.receivedTime || orders.value[index].receivedTime,
        expireTime: normalizedUpdate.expireTime || orders.value[index].expireTime,
        settlementPending: Boolean(normalizedUpdate.settlementPending),
        message: normalizedUpdate.message || ''
      })
    }

    /**
     * 取消指定等待中的订单。
     * @param {Object} order 用户自己的邮箱订单
     * @returns {Promise<void>}
     */
    const cancelOrder = async order => {
      const normalizedOrder = normalizeOrder(order)
      if (!normalizedOrder) {
        // 无本地订单 ID 时不发起取消请求，避免服务端订单查询落到 null。
        ElMessage.error($t('mailReceive.orderFailed'))
        return
      }
      const confirmed = window.confirm($t('mailReceive.cancel') + '？')
      if (!confirmed) {
        // 用户取消二次确认时不触发后端或供应商副作用。
        return
      }
      try {
        const response = await cancelMailActivation(normalizedOrder.activationId)
        if (response.status === 200 && response.data) {
          // 以后端确认的状态更新页面，避免把未完成的取消显示成成功。
          updateOrder(response.data)
          if (response.data.status === 'CANCELED') {
            // 只有供应商和本地状态都确认后才提示取消成功。
            ElMessage.success($t('mailReceive.canceled'))
          }
        }
      } catch (error) {
        // HTTP 拦截器已显示的业务错误不重复提示。
        showErrorIfNotNotified(error, $t('mailReceive.cancelFailed'))
      }
    }

    /**
     * 复制邮箱地址或验证码到剪贴板。
     * @param {string} value 待复制文本
     * @returns {Promise<void>}
     */
    const copyText = async value => {
      if (!value) {
        // 空值没有可复制内容，不触发浏览器剪贴板写入。
        return
      }
      try {
        if (navigator.clipboard?.writeText) {
          // 安全上下文支持剪贴板 API 时优先使用系统剪贴板。
          await navigator.clipboard.writeText(value)
        } else {
          // 非安全上下文使用隐藏文本框兼容旧浏览器。
          const input = document.createElement('textarea')
          input.value = value
          input.setAttribute('readonly', '')
          input.style.position = 'fixed'
          input.style.opacity = '0'
          document.body.appendChild(input)
          input.select()
          const copied = document.execCommand('copy')
          document.body.removeChild(input)
          if (!copied) {
            // 旧浏览器复制失败时进入统一错误提示。
            throw new Error('clipboard copy failed')
          }
        }
        ElMessage.success($t('mailReceive.copied'))
      } catch (error) {
        // 剪贴板受浏览器权限限制时记录错误并通知用户。
        console.error('复制邮箱接码内容失败', error)
        ElMessage.error($t('common.failed'))
      }
    }

    /**
     * 格式化人民币金额，避免浮点格式化造成额外的小数位。
     * @param {number|string} value 人民币金额
     * @returns {string} 两位小数
     */
    const formatMoney = value => {
      const amount = Number(value)
      if (!Number.isFinite(amount)) {
        // 无效价格在页面按占位符处理。
        return '0.00'
      }
      return amount.toFixed(2)
    }

    /**
     * 计算订单剩余有效时间，避免直接暴露难以快速判断的绝对到期时间。
     * @param {string|number|Date} value 服务端到期时间
     * @returns {string} MM:SS 或 HH:MM:SS 格式的倒计时
     */
    const formatCountdown = value => {
      if (!value) {
        // 供应商未提供到期时间时不构造错误倒计时。
        return '—'
      }
      const expireTimestamp = new Date(value).getTime()
      if (Number.isNaN(expireTimestamp)) {
        // 服务端异常时间不应中断订单列表渲染。
        return '—'
      }
      const remainingSeconds = Math.max(Math.ceil((expireTimestamp - currentTime.value) / 1000), 0)
      if (remainingSeconds === 0) {
        // 到期后的订单固定显示零，避免出现负数倒计时。
        return '00:00'
      }
      const hours = Math.floor(remainingSeconds / 3600)
      const minutes = Math.floor((remainingSeconds % 3600) / 60)
      const seconds = remainingSeconds % 60
      const minuteSecond = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      if (hours > 0) {
        // 超过一小时保留小时位，便于管理员和用户判断长时订单。
        return `${String(hours).padStart(2, '0')}:${minuteSecond}`
      }
      return minuteSecond
    }

    /**
     * 从本地化字典读取订单状态文案。
     * @param {string} status 后端状态码
     * @returns {string} 可见状态文案
     */
    const statusLabel = status => {
      const key = `mailReceive.status.${status}`
      const translated = $t(key)
      if (translated === key) {
        // 未知状态保持原码，便于定位服务端新增状态。
        return status || $t('common.unknown')
      }
      return translated
    }

    /**
     * 将订单状态映射为有限集合的样式类。
     * @param {string} status 后端状态码
     * @returns {string} 样式类名
     */
    const statusClass = status => {
      const allowed = ['WAITING_CODE', 'CANCELING', 'CODE_RECEIVED', 'CANCELED', 'ERROR', 'EXPIRED']
      if (!allowed.includes(status)) {
        // 未知状态落入中性样式，不把服务端值拼接到任意 CSS 规则。
        return 'status-neutral'
      }
      return `status-${status.toLowerCase().replace('_', '-')}`
    }

    watch(
      () => [selectedSupplier.value, selectedServiceCode.value, selectedDomain.value],
      () => refreshPrice()
    )

    onMounted(async () => {
      await Promise.all([loadOptions(), loadOrders()])
      pollTimer = window.setInterval(pollOrders, 7000)
      // 倒计时仅更新当前时钟，订单数据仍由原有接口和轮询流程维护。
      countdownTimer = window.setInterval(() => {
        currentTime.value = Date.now()
      }, 1000)
    })

    onUnmounted(() => {
      if (pollTimer) {
        // 离开页面时清理验证码定时器，避免路由切换后仍持续请求。
        window.clearInterval(pollTimer)
        pollTimer = null
      }
      if (countdownTimer) {
        // 离开页面时停止倒计时重绘，避免组件卸载后仍持有计时器。
        window.clearInterval(countdownTimer)
        countdownTimer = null
      }
    })

    return {
      projects,
      domains,
      orders,
      selectedProjectKey,
      selectedDomain,
      loadingOptions,
      loadingPrice,
      loadingOrders,
      creatingOrder,
      selectedPrice,
      canActivate,
      loadOrders,
      createOrder,
      cancelOrder,
      copyText,
      formatMoney,
      formatCountdown,
      statusLabel,
      statusClass
    }
  }
}
</script>

<style scoped>
.mail-page {
  width: min(1120px, calc(100% - 32px));
  margin: 38px auto 72px;
  color: #182230;
}

.mail-panel h2 {
  margin: 0;
  color: #101828;
}

.mail-panel {
  padding: 24px;
  border: 1px solid #e4e7ec;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(16, 24, 40, 0.06);
}

.mail-select-panel {
  margin-bottom: 24px;
}

.mail-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}

.mail-panel-heading p {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
}

.mail-form-grid {
  display: grid;
  grid-template-columns: minmax(190px, 1fr) minmax(190px, 1fr) minmax(170px, 0.8fr) auto;
  align-items: end;
  gap: 16px;
}

.mail-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 700;
}

.mail-search-select {
  width: 100%;
}

.mail-search-select :deep(.el-select__wrapper) {
  min-height: 46px;
  padding: 0 12px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 0 0 1px #d0d5dd inset;
}

.mail-search-select :deep(.el-select__wrapper.is-hovering:not(.is-focused)) {
  box-shadow: 0 0 0 1px #d0d5dd inset;
}

.mail-search-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #2563eb inset;
}

.mail-search-select :deep(.el-select__selected-item) {
  color: #101828;
}

.mail-search-select :deep(.el-select__caret) {
  color: #98a2b3;
}

:global(.mail-search-popper .el-select-dropdown__item) {
  color: #344054;
  font-weight: 400;
}

:global(.mail-search-popper .el-select-dropdown__item.is-hovering:not(.is-selected)) {
  background: #f8fafc;
}

:global(.mail-search-popper .el-select-dropdown__item.is-selected) {
  color: #2563eb;
  font-weight: 700;
  background: #eff6ff;
}

.mail-price-card {
  display: flex;
  flex-direction: column;
  min-height: 68px;
  justify-content: center;
  gap: 3px;
}

.mail-price-label,
.mail-order-label {
  color: #667085;
  font-size: 12px;
}

.mail-price-card strong {
  color: #b42318;
  font-size: 21px;
}

.mail-price-loading {
  display: flex;
  align-items: center;
  min-height: 38px;
}

.mail-price-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #d0d5dd;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: mail-spin 0.7s linear infinite;
}

.mail-stock {
  color: #039855;
  font-size: 12px;
}

.mail-no-stock {
  color: #b54708;
  font-size: 12px;
}

.mail-primary-button,
.mail-secondary-button,
.mail-copy-button,
.mail-cancel-button {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 10px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.mail-primary-button {
  min-width: 176px;
  border: 0;
  background: #2563eb;
  color: #fff;
}

.mail-primary-button:disabled,
.mail-secondary-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mail-secondary-button,
.mail-copy-button {
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;
}

.mail-cancel-button {
  border: 1px solid #fecdca;
  background: #fff;
  color: #b42318;
}

.mail-spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: mail-spin 0.7s linear infinite;
}

@keyframes mail-spin {
  to {
    transform: rotate(360deg);
  }
}

.mail-empty-state {
  padding: 28px 12px;
  color: #667085;
  text-align: center;
}

.mail-orders-panel {
  min-height: 220px;
}

.mail-orders-heading {
  margin-bottom: 18px;
}

.mail-order-list {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
}

.mail-order-card {
  display: grid;
  grid-template-areas:
    "title address actions"
    "meta meta actions";
  grid-template-columns: minmax(130px, 0.7fr) minmax(360px, 2fr) auto;
  align-items: center;
  column-gap: 16px;
  row-gap: 10px;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #fcfcfd;
}

.mail-order-top,
.mail-order-address,
.mail-code-box,
.mail-order-meta,
.mail-order-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mail-order-top {
  grid-area: title;
  margin: 0;
}

.mail-order-title {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.mail-order-title strong {
  overflow: hidden;
  color: #101828;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mail-order-title span,
.mail-order-meta {
  color: #667085;
  font-size: 12px;
}

.mail-status {
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: #f2f4f7;
  color: #475467;
  font-size: 12px;
  font-weight: 700;
}

.status-waiting-code,
.status-canceling {
  background: #fffaeb;
  color: #b54708;
}

.status-code-received {
  background: #ecfdf3;
  color: #027a48;
}

.status-canceled,
.status-error,
.status-expired {
  background: #fef3f2;
  color: #b42318;
}

.mail-order-address {
  display: flex;
  grid-area: address;
  min-width: 0;
  align-items: center;
  gap: 18px;
}

.mail-address-section {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
}

.mail-address-section strong {
  min-width: 0;
  color: #101828;
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.mail-address-value {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px;
  align-items: start;
  min-width: 0;
  gap: 6px;
}

.mail-copy-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.mail-copy-icon:hover {
  background: #eff6ff;
  color: #2563eb;
}

.mail-copy-icon-symbol {
  display: block;
  width: 16px;
  height: 16px;
  font-size: 16px;
  stroke: currentColor;
}

.mail-copy-icon:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.mail-code-box {
  display: grid;
  flex: 0 0 55%;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "label label"
    "value value"
    "status status";
  gap: 2px 8px;
  padding-left: 14px;
  border-left: 1px solid #d0d5dd;
}

.mail-code-ready {
  border-left-color: #86efac;
}

.mail-code-box > .mail-order-label {
  grid-area: label;
}

.mail-code-value {
  display: flex;
  grid-area: value;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mail-code-box strong {
  min-width: 0;
  color: #101828;
  font-size: 17px;
  letter-spacing: 0.08em;
}

.mail-code-status {
  grid-area: status;
  color: #667085;
  font-size: 12px;
}

.mail-order-meta {
  grid-area: meta;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.mail-settlement-note {
  grid-column: 1 / 3;
  margin: 0;
  color: #b54708;
  font-size: 12px;
}

.mail-order-actions {
  grid-area: actions;
  align-self: stretch;
  flex-direction: column;
  align-items: flex-end;
  margin: 0;
}

.mail-polling-indicator {
  color: #667085;
  font-size: 12px;
}

@media (max-width: 760px) {
  .mail-page {
    width: min(100% - 24px, 600px);
    margin-top: 22px;
  }

  .mail-panel {
    padding: 18px;
    border-radius: 14px;
  }

  .mail-form-grid {
    grid-template-columns: 1fr;
  }

  .mail-primary-button {
    width: 100%;
  }

  .mail-order-card {
    display: block;
    padding: 14px;
  }

  .mail-order-address,
  .mail-settlement-note,
  .mail-order-actions,
  .mail-order-meta {
    margin-top: 12px;
  }

  .mail-order-address {
    display: block;
  }

  .mail-code-box {
    margin-top: 12px;
    padding-top: 12px;
    padding-left: 0;
    border-top: 1px solid #d0d5dd;
    border-left: 0;
  }

  .mail-code-ready {
    border-top-color: #86efac;
  }

  .mail-order-actions {
    align-items: center;
    flex-direction: row;
  }

  .mail-order-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}
</style>
